import { apiService } from './api';
import { Property, PropertyFilters, PropertyFormData, ApiResponse, PaginatedResponse, BulkImportResponse } from '../types';

export const getProperties = async (filters: PropertyFilters = {}): Promise<PaginatedResponse<Property>> => {
  const params = new URLSearchParams();
  
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      if (Array.isArray(value)) {
        value.forEach(item => params.append(`${key}[]`, String(item)));
      } else {
        params.append(key, String(value));
      }
    }
  });

  const response = await apiService.get(`/properties?${params.toString()}`);
  return response.data;
};
export const getPropertiesList = async (filters: PropertyFilters = {}): Promise<PaginatedResponse<Property>> => {
  const params = new URLSearchParams();

  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      if (Array.isArray(value)) {
        value.forEach(item => params.append(`${key}[]`, String(item)));
      } else {
        params.append(key, String(value));
      }
    }
  });

  const response = await apiService.get(`/properties/list?${params.toString()}`);
  return response.data;
};

export const getProperty = async (id: string): Promise<ApiResponse<Property>> => {
  const response = await apiService.get(`/properties/${id}`);
  return response.data;
};

export const createProperty = async (propertyData: PropertyFormData): Promise<ApiResponse<Property>> => {
  const response = await apiService.post('/properties', propertyData);
  return response.data;
};

export const updateProperty = async (id: string, propertyData: Partial<PropertyFormData>): Promise<ApiResponse<Property>> => {
  const response = await apiService.put(`/properties/${id}`, propertyData);
  return response.data;
};

export const updatePropertySection = async (
  id: string, 
  section: string, 
  data: any, 
  opts?: { cascadeToUnits?: boolean }
): Promise<ApiResponse<Property>> => {
  const response = await apiService.put(`/properties/${id}/section`, {
    section,
    data,
    opts
  });
  return response.data;
};

export const deleteProperty = async (id: string): Promise<ApiResponse> => {
  const response = await apiService.delete(`/properties/${id}`);
  return response.data;
};

export const searchProperties = async (query: string, filters: PropertyFilters = {}): Promise<PaginatedResponse<Property>> => {
  const params = new URLSearchParams({ search: query });
  
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      if (Array.isArray(value)) {
        value.forEach(item => params.append(`${key}[]`, String(item)));
      } else {
        params.append(key, String(value));
      }
    }
  });

  const response = await apiService.get(`/properties/search?${params.toString()}`);
  return response.data;
};

export const getPropertyImages = async (id: string): Promise<ApiResponse<string[]>> => {
  const response = await apiService.get(`/properties/${id}/images`);
  return response.data;
};

export const uploadPropertyImages = async (id: string, images: FileList): Promise<ApiResponse<string[]>> => {
  const formData = new FormData();
  Array.from(images).forEach(image => {
    formData.append('images', image);
  });

  const response = await apiService.post(`/properties/${id}/images`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return response.data;
};

export const deletePropertyImage = async (propertyId: string, imageUrl: string): Promise<ApiResponse> => {
  const response = await apiService.delete(`/properties/${propertyId}/images`, {
    data: { imageUrl }
  });
  return response.data;
};

export const togglePropertyFeatured = async (id: string): Promise<ApiResponse<Property>> => {
  const response = await apiService.patch(`/properties/${id}/featured`);
  return response.data;
};

export const getPropertyAnalytics = async (id: string): Promise<ApiResponse<{
  views: number;
  totalRevenue: number;
  occupancyRate: number;
  totalRentalUnits: number;
  occupiedUnits: number;
  availableUnits: number;
  averageRent: number;
  monthlyRevenue: number;
}>> => {
  const response = await apiService.get(`/properties/${id}/analytics`);
  return response.data;
};

export const getNearbyProperties = async (lat: number, lng: number, radius: number = 5): Promise<ApiResponse<Property[]>> => {
  const response = await apiService.get(`/properties/nearby?lat=${lat}&lng=${lng}&radius=${radius}`);
  return response.data;
};

export const getUserProperties = async (): Promise<PaginatedResponse<Property>> => {
  const response = await apiService.get('/properties/user');
  return response.data;
};

// Bulk update property occupancy status based on rental units
export const bulkUpdatePropertyOccupancy = async (data: {
  companyIds?: string[];
  propertyId?: string;
}): Promise<ApiResponse<{
  totalPropertiesProcessed: number;
  propertiesUpdated: Array<{
    propertyId: string;
    name: string;
    success: boolean;
    occupancyStatus?: {
      totalUnits: number;
      occupiedUnits: number;
      availableUnits: number;
      maintenanceUnits: number;
    };
    error?: string;
  }>;
  errors: Array<{
    propertyId: string;
    name: string;
    error: string;
  }>;
}>> => {
  const response = await apiService.post('/properties/update-occupancy', data);
  return response.data;
};

// New methods for building-level property management
export const getPropertiesByCompany = async (companyId: string, filters: PropertyFilters = {}): Promise<PaginatedResponse<Property>> => {
  const params = new URLSearchParams({ companyId });
  
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      if (Array.isArray(value)) {
        value.forEach(item => params.append(`${key}[]`, String(item)));
      } else {
        params.append(key, String(value));
      }
    }
  });

  const response = await apiService.get(`/properties?${params.toString()}`);
  return response.data;
};

export const getPropertyOccupancyStats = async (id: string): Promise<ApiResponse<{
  totalUnits: number;
  occupiedUnits: number;
  availableUnits: number;
  maintenanceUnits: number;
  occupancyRate: number;
  availabilityRate: number;
}>> => {
  const response = await apiService.get(`/properties/${id}/occupancy`);
  return response.data;
};

export const getPropertyRentalUnits = async (id: string): Promise<ApiResponse<any[]>> => {
  const response = await apiService.get(`/properties/${id}/rental-units`);
  return response.data;
};

export const updatePropertyOccupancy = async (id: string, occupancyData: {
  totalUnits?: number;
  occupiedUnits?: number;
  availableUnits?: number;
  maintenanceUnits?: number;
}): Promise<ApiResponse<Property>> => {
  const response = await apiService.put(`/properties/${id}/occupancy`, occupancyData);
  return response.data;
};

// Helper functions for the new structure
export const calculateOccupancyRate = (property: Property): number => {
  if (property.occupancyStatus.totalUnits === 0) return 0;
  return (property.occupancyStatus.occupiedUnits / property.occupancyStatus.totalUnits) * 100;
};

export const calculateAvailabilityRate = (property: Property): number => {
  if (property.occupancyStatus.totalUnits === 0) return 0;
  return (property.occupancyStatus.availableUnits / property.occupancyStatus.totalUnits) * 100;
};

export const getPrimaryImage = (property: Property): string | null => {
  const primary = property.images.find(img => img.isPrimary);
  return primary ? primary.url : (property.images.length > 0 ? property.images[0].url : null);
};
export const updatePropertyVerificationStatus = async (
  id: string,
  status: 'pending' | 'verified' | 'rejected'
): Promise<ApiResponse<Property>> => {
  const response = await apiService.patch(`/properties/${id}/verification-status`, { status });
  return response.data;
};

// Property Transfer Methods
export const getCompaniesByAdminEmail = async (email: string): Promise<ApiResponse<any[]>> => {
  const response = await apiService.get(`/properties/transfer/companies?email=${encodeURIComponent(email)}`);
  return response.data;
};

export const getPreTransferSummary = async (
  propertyId: string,
  transferOptions: {
    includeMaintenanceRequests: boolean;
    includeInvoices: boolean;
    includeInactiveTenants: boolean;
  }
): Promise<ApiResponse<any>> => {
  const params = new URLSearchParams({
    includeMaintenanceRequests: String(transferOptions.includeMaintenanceRequests),
    includeInvoices: String(transferOptions.includeInvoices),
    includeInactiveTenants: String(transferOptions.includeInactiveTenants)
  });
  const response = await apiService.get(`/properties/${propertyId}/transfer/summary?${params.toString()}`);
  return response.data;
};

export const schedulePropertyTransfer = async (data: {
  propertyId: string;
  targetAdminEmail: string;
  scheduledDate?: string;
  reason?: string;
  transferNotes?: string;
  transferOptions: {
    includeMaintenanceRequests: boolean;
    includeInvoices: boolean;
    includeInactiveTenants: boolean;
  };
}): Promise<ApiResponse<any>> => {
  const response = await apiService.post(`/properties/${data.propertyId}/transfer/schedule`, data);
  return response.data;
};

export const cancelPropertyTransfer = async (
  transferId: string,
  propertyId: string,
  cancellationReason?: string
): Promise<ApiResponse<any>> => {
  const response = await apiService.delete(`/properties/${propertyId}/transfer/${transferId}`, {
    data: {
      cancellationReason
    }
  });
  return response.data;
};

export const getScheduledTransfers = async (propertyId: string): Promise<ApiResponse<any[]>> => {
  const response = await apiService.get(`/properties/${propertyId}/transfer/scheduled`);
  return response.data;
};

// Bulk import properties
export const bulkImportProperties = async (properties: PropertyFormData[]): Promise<BulkImportResponse<Property>> => {
  const response = await apiService.post('/properties/bulk-import', { properties });
  return response.data;
};
